import { CERTIFICATE_PREVIEW_LABELS } from '../../constants/labels';

const LOGO_SRC = '/pozytronLogo.svg';

/** Repeated logo header — present at the top of every certificate page. */
export const CertificateLogo = () => (
  <div className="flex justify-center">
    <img
      src={LOGO_SRC}
      alt="Pozytron"
      className="w-[290px] h-[68px] shrink-0"
    />
  </div>
);

// ─── CertificateSignatures ────────────────────────────────────────────────────

interface CertificateSignaturesProps {
  includeTechnicianDetail: boolean;
  footerText: string;
  /** When false, the footer text is suppressed here (rendered separately by CertificateLayout). */
  showFooter?: boolean;
  /** When provided, renders the actual technician signature image above the line. */
  technicianSignatureUrl?: string | null;
  /** When provided, renders the actual client signature image above the line. */
  clientSignatureUrl?: string | null;
  /** Technician name shown below the signature line when `includeTechnicianDetail` is true. */
  technicianName?: string | null;
  /** Technician business ID (e.g. TECH00000157), shown alongside the name. */
  technicianBusinessId?: string | null;
}

/** Technician + client signature lines and the footer — last page only. */
export const CertificateSignatures = ({
  includeTechnicianDetail,
  footerText,
  showFooter = true,
  technicianSignatureUrl,
  clientSignatureUrl,
  technicianName,
  technicianBusinessId,
}: CertificateSignaturesProps) => (
  <div className="flex flex-col items-center w-full gap-8.25">
    <div className="flex items-start justify-between w-full">
      {/* Technician signature */}
      <div
        className={`flex flex-col gap-2 items-center ${includeTechnicianDetail ? 'w-[256px] px-8' : 'w-59 px-5.5'}`}
      >
        {technicianSignatureUrl && (
          <img
            src={technicianSignatureUrl}
            alt={CERTIFICATE_PREVIEW_LABELS.TECHNICIAN_SIGNATURE}
            className="h-16 w-auto object-contain mb-1"
          />
        )}
        <div className="border-t-[1.6px] border-[#DCDDE0] w-full" />
        <div className="flex flex-col gap-1 items-center">
          <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-center text-[#474A4C] w-full wrap-break-word">
            {CERTIFICATE_PREVIEW_LABELS.TECHNICIAN_SIGNATURE}
          </p>
          {includeTechnicianDetail && (
            <p className="font-roboto font-medium text-[14px] leading-5 tracking-[0.25px] text-center text-[#181A1B] wrap-break-word">
              {technicianName ? (
                technicianBusinessId ? (
                  <>
                    {technicianName}{' '}
                    {/* nowrap keeps "(BUSINESS_ID)" atomic — if it doesn't
                        fit on the current line it moves whole to the next
                        line instead of splitting/hyphenating mid-token. */}
                    <span className="whitespace-nowrap">
                      ({technicianBusinessId})
                    </span>
                  </>
                ) : (
                  technicianName
                )
              ) : (
                CERTIFICATE_PREVIEW_LABELS.TECHNICIAN_DETAILS_PLACEHOLDER
              )}
            </p>
          )}
        </div>
      </div>

      {/* Client signature */}
      <div className="w-[256px] px-8 flex flex-col gap-2 items-center ml-auto">
        {clientSignatureUrl && (
          <img
            src={clientSignatureUrl}
            alt={CERTIFICATE_PREVIEW_LABELS.CLIENT_SIGNATURE}
            className="h-16 w-auto object-contain mb-1"
          />
        )}
        <div className="border-t-[1.6px] border-[#DCDDE0] w-full" />
        <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-center text-[#474A4C] w-full wrap-break-word">
          {CERTIFICATE_PREVIEW_LABELS.CLIENT_SIGNATURE}
        </p>
      </div>
    </div>

    {showFooter && (
      <p className="font-roboto font-medium text-[12px] leading-4 tracking-[0.4px] text-center text-[#2A4455] w-full">
        {footerText}
      </p>
    )}
  </div>
);

// ─── CertificateLayout ────────────────────────────────────────────────────────

interface CertificateLayoutProps {
  includeTechnicianDetail: boolean;
  footerText: string;
  children: React.ReactNode;
  /**
   * When true (default) the signature lines are rendered, pinned to the bottom
   * of the available space. On a multi-page certificate this is set only on the
   * last page.
   */
  showSignatures?: boolean;
  /**
   * When true (default) signatures are pushed to the bottom of the page with
   * `mt-auto`. Set to false when the signature page has no content blocks so
   * signatures appear at the top instead of leaving a blank gap above them.
   * The footer is always pinned to the very bottom regardless of this flag.
   */
  pinSignaturesBottom?: boolean;
  /** Passed through to {@link CertificateSignatures} — actual technician signature image URL. */
  technicianSignatureUrl?: string | null;
  /** Passed through to {@link CertificateSignatures} — actual client signature image URL. */
  clientSignatureUrl?: string | null;
  /** Passed through to {@link CertificateSignatures} — technician display name. */
  technicianName?: string | null;
  /** Passed through to {@link CertificateSignatures} — technician business ID. */
  technicianBusinessId?: string | null;
}

/**
 * The bordered certificate frame: repeated logo header at the top, the page
 * content below it, and — on the last page — the signature block.
 *
 * The footer text is always rendered at the very bottom of every page,
 * independent of whether signatures are present or where they sit.
 *
 * Signature positioning:
 *  - `pinSignaturesBottom=true` (default): signatures pushed down with `mt-auto`;
 *    footer follows naturally.
 *  - `pinSignaturesBottom=false`: signatures flow after content; footer gets its
 *    own `mt-auto` to stay at the bottom.
 */
export const CertificateLayout = ({
  includeTechnicianDetail,
  footerText,
  children,
  showSignatures = true,
  pinSignaturesBottom = true,
  technicianSignatureUrl,
  clientSignatureUrl,
  technicianName,
  technicianBusinessId,
}: CertificateLayoutProps) => (
  <div className="relative w-full h-full rounded-[10px] border-[1.6px] border-[rgba(42,68,85,0.3)] px-8 py-6 flex flex-col">
    <div className="mb-8">
      <CertificateLogo />
    </div>

    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex flex-col gap-10">{children}</div>

      {showSignatures && (
        <div className={`${pinSignaturesBottom ? 'mt-auto' : ''} pt-8`}>
          <CertificateSignatures
            includeTechnicianDetail={includeTechnicianDetail}
            footerText={footerText}
            showFooter={false}
            {...(technicianSignatureUrl !== undefined
              ? { technicianSignatureUrl }
              : {})}
            {...(clientSignatureUrl !== undefined
              ? { clientSignatureUrl }
              : {})}
            {...(technicianName !== undefined ? { technicianName } : {})}
            {...(technicianBusinessId !== undefined
              ? { technicianBusinessId }
              : {})}
          />
        </div>
      )}

      {/*
       * Footer is pinned to the very bottom of every page.
       * When signatures already carry `mt-auto` (pinSignaturesBottom=true),
       * the footer follows them naturally — adding a second `mt-auto` would
       * split remaining space. When signatures are absent or float near the
       * top, the footer takes its own `mt-auto` to anchor to the bottom.
       */}
      <p
        className={`${!showSignatures || !pinSignaturesBottom ? 'mt-auto' : ''} pt-4 font-roboto font-medium text-[12px] leading-4 tracking-[0.4px] text-center text-[#2A4455] w-full`}
      >
        {footerText}
      </p>
    </div>

    {/* Success badge — anchored to the bottom-right of every page, above the footer+sig area */}
    <img
      src="/certSuccessIcon.svg"
      alt=""
      aria-hidden="true"
      className="absolute pointer-events-none select-none"
      style={{ width: 52, height: 52, right: 18, bottom: 130 }}
    />
  </div>
);
